"use client";

import { Button } from "../buttons/button";
import { cn } from "@drpcrd/utils";
import { ArrowDown } from "lucide-react";
import type { ComponentProps } from "react";
import { useCallback, useEffect, useRef } from "react";
import { StickToBottom, useStickToBottomContext } from "use-stick-to-bottom";

export type ChatConversationContentProps = ComponentProps<
  typeof StickToBottom.Content
>;

const ChatConversationContent = ({
  className,
  ...props
}: ChatConversationContentProps) => (
  <StickToBottom.Content
    className={cn("flex flex-col space-y-4", className)}
    {...props}
  />
);

type ChatConversationScrollButtonProps = ComponentProps<typeof Button>;

const ChatConversationScrollButton = ({
  className,
  ...props
}: ChatConversationScrollButtonProps) => {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  const handleScrollToBottom = useCallback(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  return (
    !isAtBottom && (
      <Button
        className={cn(
          "absolute bottom-4 left-[50%] h-fit w-fit translate-x-[-50%] rounded-full",
          className,
        )}
        onClick={handleScrollToBottom}
        size="sm"
        variant="filled"
        {...props}
      >
        Back to bottom <ArrowDown size={36} strokeWidth={4} />
      </Button>
    )
  );
};

type ChatConversationRepinProps = {
  trigger: number | string;
};

/**
 * Scrolls the conversation back to the bottom whenever `trigger` changes,
 * even if the user had scrolled up. Render inside a ChatConversation and
 * pass e.g. the number of user messages, so sending a message re-pins the
 * view without interrupting someone re-reading during a stream.
 */
export const ChatConversationRepin = ({
  trigger,
}: ChatConversationRepinProps) => {
  const { scrollToBottom } = useStickToBottomContext();
  const previousTrigger = useRef(trigger);

  useEffect(() => {
    if (previousTrigger.current !== trigger) {
      previousTrigger.current = trigger;
      scrollToBottom();
    }
  }, [trigger, scrollToBottom]);

  return null;
};

export type ChatConversationProps = ComponentProps<typeof StickToBottom> & {
  hideScrollButton?: boolean;
};

export const ChatConversation = ({
  className,
  children,
  hideScrollButton = false,
  ...props
}: ChatConversationProps) => (
  <StickToBottom
    className={cn("relative flex-1 overflow-y-auto", className)}
    initial="smooth"
    resize="smooth"
    role="log"
    {...props}
  >
    <ChatConversationContent>{children}</ChatConversationContent>
    {!hideScrollButton && <ChatConversationScrollButton />}
  </StickToBottom>
);
